import SignaturePad from 'signature_pad';
import Swal from 'sweetalert2';
import {MaterialSelect} from "../../components/material-select";
import {MaterialModal} from "../../shared/material-modal";
import {Utils} from "../../shared/Utils";
import flatpickr from 'flatpickr';
import {Instance} from "flatpickr/dist/types/instance";
import 'flatpickr/dist/flatpickr.min.css';
import {ApplicationService} from "../../shared/services/application-service";
import {compressImage, formatFileSize} from "../../shared/image-compression";
import {isGoodConnection} from "../../shared/connection-quality";

declare const minMoveInDate: string;
declare const moveInFromByYear: { [year: string]: number[] | string };

export class Application {
    public static instance: Application;

    private currentStep = 1;
    private isExistingStudent = false;
    private readonly walkIn: boolean = false;
    private signaturePad!: SignaturePad;

    /**
     * One id per page load, tying this visit's funnel events together (P4 of the
     * building-selection redesign). It also rides along on the submit itself, so the server's
     * outcome events join the same funnel. randomUUID needs a secure context, hence the fallback.
     */
    private readonly attemptId: string = crypto?.randomUUID?.()
        ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    private readonly loadedAt = performance.now();

    /** P2 grid: present only when the per-client toggle is on (integrations page). */
    private readonly buildingGridEl = document.getElementById('buildingGrid');
    private buildingSheet: MaterialModal | null = null;
    /** The building the open sheet is showing — the two action buttons read this. */
    private sheetBuilding: string | null = null;
    /** The room type picked in the open sheet; the Choose CTA is disabled until it is set. */
    private sheetTypeId: number | null = null;
    /** True while programmatically assigning the building select (deep link / single building). */
    private suppressBuildingTrack = false;
    /** The in-flight room-type reload for the chosen building; choose-from-sheet awaits it. */
    private preferenceLoad: Promise<void> | null = null;
    private typeFilterSelect: MaterialSelect | null = null;

    /** 5 when the residence grid is its own step, 4 classic — the server renders the steps. */
    private readonly totalSteps = document.querySelectorAll('.form-step').length;

    /**
     * The blob to send for each file input, keyed by the input's name — compressed for images,
     * the original file for everything else.
     *
     * <p>Held here because {@code input.files} is read-only: the shrunken image cannot be put back
     * onto the element it came from, so submit has to read from here instead. An input missing
     * from this map has nothing chosen, or had its choice rejected.</p>
     */
    private readonly preparedUploads = new Map<string, Blob>();

    private readonly formElement = document.getElementById('applicationForm') as HTMLFormElement;

    // Step 1 Elements
    private readonly yearInput = document.getElementById('year') as HTMLInputElement;
    private readonly yearButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('#yearSwitch .year-option'));
    private readonly firstNameInput = document.getElementById('firstName') as HTMLInputElement;
    private readonly lastNameInput = document.getElementById('lastName') as HTMLInputElement;
    private readonly studentNumberInput = document.getElementById('studentNumber') as HTMLInputElement;
    private readonly studentNumberFeedback = document.getElementById('studentNumberFeedback') as HTMLDivElement;
    private readonly idNumberInput = document.getElementById('idNumber') as HTMLInputElement;
    private readonly idNumberGroup = document.getElementById('idNumber-group') as HTMLDivElement;
    private readonly identityTypeGroup = document.getElementById('identityType-group') as HTMLDivElement;
    private readonly idNumberFeedback = document.getElementById('idNumberFeedback') as HTMLDivElement;
    private readonly idNumberLabel = document.getElementById('idNumberLabel') as HTMLSpanElement;
    private identityTypeSelect!: MaterialSelect;
    private readonly phoneNumberInput = document.getElementById('phoneNumber') as HTMLInputElement;
    private readonly phoneNumberGroup = document.getElementById('phoneNumber-group') as HTMLDivElement;
    private readonly whatsappNumberInput = document.getElementById('whatsappNumber') as HTMLInputElement;
    private readonly residentialAddressInput = document.getElementById('residentialAddress') as HTMLInputElement;
    private readonly residentialAddressGroup = document.getElementById('residentialAddress-group') as HTMLDivElement;
    private readonly emailInput = document.getElementById('email') as HTMLInputElement;
    private readonly confirmEmailInput = document.getElementById('confirmEmail') as HTMLInputElement;
    private readonly emailMatchFeedback = document.getElementById('emailMatchFeedback') as HTMLDivElement;
    private readonly studentEmailInput = document.getElementById('studentEmail') as HTMLInputElement;
    private genderSelect!: MaterialSelect;

    // Step 2 Elements
    private readonly courseInput = document.getElementById('course') as HTMLInputElement;
    private yearOfStudySelect!: MaterialSelect;
    private campusSelect!: MaterialSelect;
    private buildingSelect!: MaterialSelect;
    private preferenceSelect!: MaterialSelect;
    private alternativeBuildingsSelect!: MaterialSelect;
    private methodOfPaymentSelect!: MaterialSelect;
    private preferredMoveInDate!: Instance;
    private readonly budgetInput = document.getElementById('budget') as HTMLInputElement;
    private readonly budgetGroup = document.getElementById('budget-group') as HTMLDivElement;
    private readonly bursaryInput = document.getElementById('bursary') as HTMLInputElement;
    private readonly bursaryGroup = document.getElementById('bursary-group') as HTMLDivElement;
    private readonly specialRequirementsTextArea = document.getElementById('specialRequirements') as HTMLTextAreaElement;
    private refererSelect!: MaterialSelect;
    private readonly refererDetailsTextarea = document.getElementById('refererDetails') as HTMLTextAreaElement;
    private readonly refererDetailsGroup = document.getElementById('referer-details-group') as HTMLDivElement;

    // Step 3 Elements
    private readonly guardianNameInput = document.getElementById('guardianName') as HTMLInputElement;
    private readonly guardianNameGroup = document.getElementById('guardianName-group') as HTMLDivElement;
    private readonly guardianPhoneInput = document.getElementById('guardianPhone') as HTMLInputElement;
    private readonly guardianPhoneGroup = document.getElementById('guardianPhone-group') as HTMLDivElement;
    private readonly guardianWorkInput = document.getElementById('guardianWork') as HTMLInputElement;
    private readonly guardianWorkGroup = document.getElementById('guardianWork-group') as HTMLDivElement;
    private readonly guardianEmailInput = document.getElementById('guardianEmail') as HTMLInputElement;
    private readonly guardianEmailGroup = document.getElementById('guardianEmail-group') as HTMLDivElement;

    // Step 4 Elements
    private readonly signatureCanvas = document.getElementById('signaturePad') as HTMLCanvasElement;
    private readonly reviewContent = document.getElementById('reviewContent') as HTMLDivElement;
    private readonly clearSignatureButton = document.getElementById('clearSignatureBtn') as HTMLButtonElement;
    private readonly submitButton = document.getElementById('submitApplicationBtn') as HTMLButtonElement;

    // Navigation buttons
    private readonly nextStep1Btn = document.getElementById('nextStep1Btn') as HTMLButtonElement;
    private readonly nextStep2Btn = document.getElementById('nextStep2Btn') as HTMLButtonElement;
    private readonly nextStep3Btn = document.getElementById('nextStep3Btn') as HTMLButtonElement;
    private readonly prevStep2Btn = document.getElementById('prevStep2Btn') as HTMLButtonElement;
    private readonly prevStep3Btn = document.getElementById('prevStep3Btn') as HTMLButtonElement;
    private readonly prevStep4Btn = document.getElementById('prevStep4Btn') as HTMLButtonElement;

    constructor() {
        const submitBtn = document.getElementById('submitApplicationBtn') as HTMLButtonElement;
        if (submitBtn.dataset.walkIn) {
            this.walkIn = submitBtn.dataset.walkIn === 'true';
        }

        this.initializeMaterializeSelects();
        this.initBuildingGrid();
        this.initializeSignaturePad();
        this.attachEventListeners();
        // --- NEW --- Call the file validation method
        this.attachFileValidationListeners();
        this.updateStepUI();
        this.updatePaymentDependentFields();
        this.updateRefererDependentFields();

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('im-testing')) {
            this.autofillForTesting();
        }

        this.track('PAGE_LOAD', {
            screen: `${screen.width}x${screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            touch: window.matchMedia?.('(pointer: coarse)').matches ?? false,
            referrer: document.referrer?.slice(0, 300) || null,
            walkIn: this.walkIn,
        });
    }

    /**
     * P4 funnel beacon. Fire-and-forget: analytics must never cost an applicant anything, so
     * every failure is swallowed and nothing ever awaits it. The server drops any event name
     * it does not recognise.
     */
    private track(event: string, payload: Record<string, unknown> = {}): void {
        try {
            const body = JSON.stringify({attemptId: this.attemptId, event, payload});
            navigator.sendBeacon?.('/apply/event', new Blob([body], {type: 'application/json'}));
        } catch {
            // Never let analytics interfere with the form.
        }
    }

    private selectYear(btn: HTMLButtonElement): void {
        this.yearButtons.forEach(b => {
            b.classList.toggle('active', b === btn);
            b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        this.yearInput.value = btn.dataset.year || '';
        this.track('YEAR_CHOSEN', {year: this.yearInput.value});
        // Availability counts are per application year, so switching year after picking a
        // building must not leave the other year's "(N Available)" numbers on screen.
        if (this.buildingSelect?.selected) {
            this.updatePreference(this.buildingSelect.selected);
        }
        // The datepicker minimum follows the chosen year's intake ("earliest move-in" on the
        // lanes). LocalDate arrives from Thymeleaf inline as [y, m, d].
        const floor = typeof moveInFromByYear !== 'undefined' ? moveInFromByYear[this.yearInput.value] : undefined;
        if (floor && this.preferredMoveInDate) {
            const min = Array.isArray(floor) ? new Date(floor[0], floor[1] - 1, floor[2]) : new Date(floor);
            this.preferredMoveInDate.set('minDate', min);
        }
    }

    private initializeMaterializeSelects(): void {
        this.yearButtons.forEach(btn => btn.addEventListener('click', () => this.selectYear(btn)));
        this.genderSelect = MaterialSelect.init('#gender', {});
        this.identityTypeSelect = MaterialSelect.init('#identityType', {
            onSelect: () => this.validateIdNumber()
        });
        this.yearOfStudySelect = MaterialSelect.init('#yearOfStudy', {});
        this.campusSelect = MaterialSelect.init('#campus', {});

        this.refererSelect = MaterialSelect.init('#referer', {
            onSelect: () => this.updateRefererDependentFields()
        });

        this.buildingSelect = MaterialSelect.init('#building', {
            // The single handler for a building choice however it happens: the dropdown, the
            // grid (which writes into this select), or the test autofill. The setter fires
            // onSelect, so the grid path must not duplicate any of this.
            onSelect: selectedItems => {
                if (selectedItems.length > 0) {
                    // Held so choose-from-sheet can apply the picked room type AFTER the
                    // options it belongs to have actually loaded.
                    this.preferenceLoad = this.updatePreference(selectedItems[0].value);
                    // The baseline-vs-grid metric: how often the choice moves off the default.
                    // Building names carry no personal data.
                    if (!this.suppressBuildingTrack) {
                        this.track('BUILDING_CHOSEN', {
                            building: selectedItems[0].value,
                            via: this.buildingGridEl ? 'grid' : 'dropdown',
                        });
                    }
                    this.syncGridState();
                }
            },
            searchable: true,
        });

        if(this.buildingSelect.selected){
            this.updatePreference(this.buildingSelect.selected);
        }

        this.preferenceSelect = MaterialSelect.init('#preference', {});
        this.alternativeBuildingsSelect = MaterialSelect.init('#alternativeBuildings', {
            searchable: true,
            multiSelect: true
        });
        this.methodOfPaymentSelect = MaterialSelect.init('#methodOfPayment', {
            onSelect: () => this.updatePaymentDependentFields()
        });
        this.preferredMoveInDate = flatpickr('#preferredMoveInDate', {
            dateFormat: 'M d, Y',
            minDate: Utils.stringToDateISO(minMoveInDate) || new Date(),
            // On phones flatpickr swaps itself for the OS-native date input, which shows a
            // bare dd/mm/yyyy text box on Android — keep the calendar popup everywhere.
            disableMobile: true,
        }) as Instance;
    }

    private initializeSignaturePad(): void {
        if (!this.signatureCanvas) return;

        setTimeout(() => {
            this.resizeCanvas();
            this.signaturePad = new SignaturePad(this.signatureCanvas, {
                backgroundColor: 'rgb(255, 255, 255)',
                penColor: 'rgb(0, 0, 0)',
                minWidth: 0.5,
                maxWidth: 2.5,
            });

            if (window.ResizeObserver) {
                const resizeObserver = new ResizeObserver(() => {
                    this.resizeCanvas();
                });
                resizeObserver.observe(this.signatureCanvas.parentElement!);
            }
        }, 100);
    }

    private attachEventListeners(): void {
        window.addEventListener("resize", () => this.resizeCanvas());
        this.nextStep1Btn.addEventListener('click', () => this.nextStep());
        // Residence-step buttons exist only for grid clients.
        document.getElementById('nextStepResBtn')?.addEventListener('click', () => this.nextStep());
        document.getElementById('prevStepResBtn')?.addEventListener('click', () => this.prevStep());
        this.nextStep2Btn.addEventListener('click', () => this.nextStep());
        this.nextStep3Btn.addEventListener('click', () => this.nextStep());
        this.prevStep2Btn.addEventListener('click', () => this.prevStep());
        this.prevStep3Btn.addEventListener('click', () => this.prevStep());
        this.prevStep4Btn.addEventListener('click', () => this.prevStep());
        this.submitButton.addEventListener('click', () => this.submitApplication());
        this.clearSignatureButton.addEventListener('click', () => this.clearSignature());

        this.studentNumberInput.addEventListener('focusout', async () => this.checkStudentExists());
        this.confirmEmailInput.addEventListener('input', () => this.validateEmailMatch());
        this.idNumberInput.addEventListener('focusout', () => this.validateIdNumber());
    }

    // --- NEW METHOD --- For frontend file validation
    private attachFileValidationListeners(): void {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
        /**
         * The server's own ceiling, per REQUEST rather than per file
         * (spring.servlet.multipart.max-request-size). Two 4.9 MB files pass the per-file check
         * and still total under it; a third document type — and those are client-configured —
         * would not, and the whole submit would fail as a raw 500 after everything had been
         * uploaded. Checked here so it fails before a byte moves.
         */
        const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB
        const ALLOWED_MIME_TYPES = [
            'application/pdf',
            'image/png',
            'image/jpeg',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];
        const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx', '.xls', '.xlsx'];

        document.querySelectorAll('input[type="file"]').forEach(input => {
            input.addEventListener('change', async (event) => {
                const target = event.target as HTMLInputElement;
                const file = target.files?.[0];

                if (!file) {
                    this.preparedUploads.delete(target.name);
                    this.renderTotalUploadSize();
                    return;
                }

                // 1. Validate file type (MIME type and extension for robustness). Cheap, and it
                //    decides whether the file is worth trying to compress at all.
                const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
                if (!ALLOWED_MIME_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
                     this.showCustomModal('Invalid File Type', `The file type of "${file.name}" is not supported. Please upload a supported file type.`, 'error');
                     target.value = ''; // Clear the invalid file selection
                     this.preparedUploads.delete(target.name);
                     this.renderTotalUploadSize();
                     return;
                }

                // 2. Shrink it now, not at submit. A 4 MB phone photo becomes ~250 KB, which is
                //    the difference between 175 seconds and 11 on the connections these students
                //    are on. Doing it here means the work happens while they fill in later steps,
                //    and the size shown below is the size that will actually be sent.
                const prepared = await compressImage(file);

                // 3. Size is judged on what will be SENT, not on what came off the camera. A 12 MP
                //    photo is routinely 8-13 MB, and refusing it before compressing would turn a
                //    file we can shrink to a quarter of a megabyte into "too large" — rejecting
                //    the exact students this change exists to help. Only what survives compression
                //    has to fit.
                if (prepared.size > MAX_FILE_SIZE) {
                    this.showCustomModal('File Too Large',
                        `The file "${file.name}" is ${formatFileSize(prepared.size)}. The maximum `
                        + `allowed size is ${formatFileSize(MAX_FILE_SIZE)}.`, 'error');
                    target.value = '';
                    this.preparedUploads.delete(target.name);
                    this.renderTotalUploadSize();
                    return;
                }
                this.preparedUploads.set(target.name, prepared);

                // 4. Now that the real payload is known, check the request total. The per-file
                //    check above cannot see it.
                const total = this.totalPreparedSize();
                if (total > MAX_TOTAL_SIZE) {
                    const largest = this.largestPreparedUpload();
                    this.showCustomModal(
                        'Files Too Large Together',
                        `Your documents come to ${formatFileSize(total)} in total, and the limit is `
                        + `${formatFileSize(MAX_TOTAL_SIZE)}. The largest is "${largest}". Please `
                        + `replace it with a smaller file or a photo.`,
                        'error');
                    target.value = '';
                    this.preparedUploads.delete(target.name);
                    this.renderTotalUploadSize();
                    return;
                }

                this.showPreparedFile(target, file, prepared);
                this.renderTotalUploadSize();

                // 5. Anything we could not shrink — a PDF, chiefly — may still be slow enough to
                //    lose the race against the gateway timeout. Advice, not a refusal.
                if (prepared.size > this.slowUploadThreshold()) {
                    this.showCustomModal(
                        'This may be slow to upload',
                        `This file is ${formatFileSize(prepared.size)} and may be slow to upload. `
                        + `Try taking clear photos of the document and uploading them as JPG images instead`,
                        'warning');
                }
            });
        });
    }

    /**
     * Above this, warn that a file may not finish uploading.
     *
     * <p>2 MB is roughly 87 seconds at the 23 KB/s measured on a student's phone during a reported
     * failure — about where a slow connection starts losing against the gateway's 100-second
     * limit. Halved when the device reports 2G or data-saver.</p>
     *
     * <p>Deliberately two values rather than a computed one: {@code effectiveType} describes the
     * <em>downlink</em>, so anything derived from it flatters an asymmetric mobile connection.</p>
     */
    private slowUploadThreshold(): number {
        return isGoodConnection() ? 2 * 1024 * 1024 : 1024 * 1024;
    }

    private totalPreparedSize(): number {
        let total = 0;
        this.preparedUploads.forEach(blob => total += blob.size);
        return total;
    }

    private largestPreparedUpload(): string {
        let name = '', size = -1;
        this.preparedUploads.forEach((blob, key) => {
            if (blob.size > size) { size = blob.size; name = key; }
        });
        return name;
    }

    /**
     * Show the size that will actually be sent — both figures for a compressed image, since
     * quoting the original would over-report by more than tenfold and quoting only the new one
     * hides that anything happened.
     */
    private showPreparedFile(target: HTMLInputElement, original: File, prepared: Blob): void {
        const fileUploadItem = target.closest('.file-upload-item') as HTMLDivElement;
        if (!fileUploadItem) return;

        const size = prepared.size < original.size
            ? `${formatFileSize(original.size)} → ${formatFileSize(prepared.size)}`
            : formatFileSize(prepared.size);

        // The readonly text input the template shows the chosen file in. The markup also sets it
        // from an inline onchange; that fires first and only has the raw name, so writing here
        // afterwards is what puts the size on screen.
        const filePath = fileUploadItem.querySelector('.file-path') as HTMLInputElement | null;
        if (filePath) filePath.value = `${original.name} (${size})`;

        fileUploadItem.classList.add('file-selected');
    }

    /** A running total, so a student who must swap something out can see where they stand. */
    private renderTotalUploadSize(): void {
        const el = document.getElementById('upload-total-size');
        if (!el) return;
        const total = this.totalPreparedSize();
        el.textContent = total === 0 ? '' : `Total: ${formatFileSize(total)} of 10 MB`;
    }

    private resizeCanvas(): void {
        if (!this.signatureCanvas) return;

        const canvas = this.signatureCanvas;
        const container = canvas.parentElement!;

        // Save existing signature data before resizing
        let signatureData: any[] = [];
        if (this.signaturePad && !this.signaturePad.isEmpty()) {
            signatureData = this.signaturePad.toData();
        }

        // Get the container's actual dimensions
        const containerRect = container.getBoundingClientRect();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        // Set canvas size to match container
        canvas.style.width = '100%';
        canvas.style.height = '200px';
        canvas.width = containerRect.width * ratio;
        canvas.height = 200 * ratio;

        const ctx = canvas.getContext("2d");
        if (ctx) {
            ctx.scale(ratio, ratio);
            canvas.style.width = containerRect.width + 'px';
            canvas.style.height = '200px';
        }

        if (this.signaturePad) {
            if (signatureData.length > 0) {
                this.signaturePad.fromData(signatureData);
            }
        }
    }

    public clearSignature(): void {
        if (this.signaturePad) {
            this.signaturePad.clear();
            this.showFeedback(document.getElementById('signatureFeedback') as HTMLDivElement, '', 'none');
        }
    }

    private getSignatureData(): string | null {
        if (!this.signaturePad || this.signaturePad.isEmpty()) {
            return null;
        }
        return this.signaturePad.toDataURL('image/png');
    }

    public nextStep(): void {
        if (!this.validateCurrentStep()) {
            return;
        }

        if (this.currentStep < this.totalSteps) {
            this.currentStep++;
            this.updateStepUI();
            this.trackStep();

            if (this.currentStep === this.totalSteps) {
                this.populateReviewStep();
                setTimeout(() => {
                    if (this.signaturePad) {
                        this.resizeCanvas();
                    }
                }, 100);
            }
        }
    }

    public prevStep(): void {
        if (this.currentStep > 1) {
            this.currentStep--;
            this.updateStepUI();
            this.trackStep();
        }
    }

    /** Every navigation lands in the funnel: step number (and of how many — the grid flow
     *  has five, the classic four) plus ms since page load. */
    private trackStep(): void {
        this.track('STEP_REACHED', {
            step: this.currentStep,
            of: this.totalSteps,
            ms: Math.round(performance.now() - this.loadedAt),
        });
    }

    private updateStepUI(): void {
        document.querySelectorAll('.form-step').forEach(step => step.classList.remove('active'));
        document.querySelector(`.form-step[data-step="${this.currentStep}"]`)?.classList.add('active');

        const progressFill = document.querySelector('.progress-fill') as HTMLDivElement;
        const progress = ((this.currentStep - 1) / (this.totalSteps - 1)) * 100;
        progressFill.style.width = `${progress}%`;

        document.querySelectorAll('.step-indicator').forEach((indicator, index) => {
            const stepNumber = index + 1;
            indicator.classList.remove('active', 'completed');
            if (stepNumber < this.currentStep) {
                indicator.classList.add('completed');
                indicator.innerHTML = '<i class="material-icons">check</i>';
            } else if (stepNumber === this.currentStep) {
                indicator.classList.add('active');
                indicator.innerHTML = `${stepNumber}`;
            } else {
                indicator.innerHTML = `${stepNumber}`;
            }
        });

        document.querySelectorAll('.step-label').forEach((label, index) => {
            const stepNumber = index + 1;
            label.classList.remove('active');
            if (stepNumber === this.currentStep) {
                label.classList.add('active');
            }
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private validateCurrentStep(): boolean {
        const currentStepElement = document.querySelector(`.form-step[data-step="${this.currentStep}"]`);
        if (!currentStepElement) return false;

        let isValid = true;

        const inputs = currentStepElement.querySelectorAll('input, textarea');
        inputs.forEach(input => {
            const field = input as HTMLInputElement | HTMLTextAreaElement;
            const formGroup = field.closest('.form-group') as HTMLDivElement;

            if (formGroup && formGroup.classList.contains('hide')) {
                return;
            }

            if (field.required && !field.value.trim()) {
                this.showFeedback(formGroup, 'This field is required', 'error');
                isValid = false;
            } else {
                this.showFeedback(formGroup, '', 'none');
            }
        });

        const selects = currentStepElement.querySelectorAll<HTMLSelectElement>('select[required]');
        selects.forEach(select => {
            if(!select.id && !select.getAttribute('name')){
                select.id = `select-${Math.random().toString(36).substr(2, 9)}`;
            }
            const selectInstance = MaterialSelect.getInstance(select.id || select.getAttribute('name') || '');
            const formGroup = select.closest('.form-group') as HTMLDivElement;

            // grid-driven: the hidden building selects — the grid check below speaks for them,
            // where the applicant is actually looking.
            if (formGroup && (formGroup.classList.contains('hide') || formGroup.classList.contains('grid-driven'))) {
                return;
            }

            if (!selectInstance?.selected) {
                this.showFeedback(formGroup, 'This field is required', 'error');
                isValid = false;
            } else {
                this.showFeedback(formGroup, '', 'none');
            }
        });

        const fileInputs = currentStepElement.querySelectorAll<HTMLInputElement>('input[type="file"][required]');
        fileInputs.forEach(fileInput => {
            const fileUploadItem = fileInput.closest('.file-upload-item') as HTMLDivElement;
            const feedbackDiv = fileUploadItem.querySelector('.feedback-message') as HTMLDivElement;
            if (fileInput?.files?.length === 0) {
                fileUploadItem.classList.add('file-upload-error');
                if (!feedbackDiv) {
                    const newFeedbackDiv = document.createElement('div');
                    newFeedbackDiv.className = 'feedback-message error-message';
                    newFeedbackDiv.innerHTML = '<i class="material-icons">error</i>This file is required';
                    fileUploadItem.appendChild(newFeedbackDiv);
                }
                isValid = false;
            } else {
                fileUploadItem.classList.remove('file-upload-error');
                if (feedbackDiv) {
                    feedbackDiv.remove();
                }
            }
        });


        if (this.currentStep === 1) {
            // The year switch starts with nothing selected when more than one year is offered —
            // a wrong default here is what silently swallowed residents' applications.
            if (!this.yearInput.value) {
                const yearGroup = this.yearInput.closest('.form-group') as HTMLDivElement;
                this.showFeedback(yearGroup, 'Please choose which year you are applying for', 'error');
                isValid = false;
            }

            if (this.emailInput.value !== this.confirmEmailInput.value) {
                this.showFeedback(this.emailMatchFeedback, 'Email addresses do not match', 'error');
                isValid = false;
            } else {
                this.showFeedback(this.emailMatchFeedback, 'Email addresses match', 'success');
            }

            // Skipped for returning applicants, whose ID is already on file and whose group is hidden.
            if (!this.idNumberGroup.classList.contains('hide') && !this.validateIdNumber()) {
                isValid = false;
            }
        }

        // P2 grid: the residence step's real inputs are the hidden building + preference
        // selects, so their "required" messages must land on the grid itself. A building
        // without a room type happens on the ?building= deep link — the sheet still owes
        // the applicant that choice.
        if (this.currentStep === 3 && this.buildingGridEl) {
            const section = document.getElementById('buildingGridSection') as HTMLDivElement;
            if (!this.buildingSelect.selected) {
                this.showFeedback(section, 'Please choose a residence to continue', 'error');
                isValid = false;
            } else if (!this.preferenceSelect.selected) {
                this.showFeedback(section, 'Tap your residence and pick a room type', 'error');
                isValid = false;
            } else {
                this.showFeedback(section, '', 'none');
            }
        }

        // A move-in date outside the chosen application year is contradictory input — the
        // application files under the chosen year, so a 2026 date on a 2027 application would
        // sail through and print on the acceptance letter.
        const moveInDate = this.preferredMoveInDate?.selectedDates?.[0];
        if (moveInDate && this.yearInput.value
                && currentStepElement.contains(this.preferredMoveInDate.input)
                && moveInDate.getFullYear() !== +this.yearInput.value) {
            const group = this.preferredMoveInDate.input.closest('.form-group') as HTMLDivElement;
            this.showFeedback(group,
                `This date is in ${moveInDate.getFullYear()}, but you are applying for ${this.yearInput.value}`, 'error');
            isValid = false;
        }

        if (!isValid) {
            this.showCustomModal('Validation Error', 'Please correct the highlighted fields before proceeding.', 'error');
        }

        return isValid;
    }

    private showFeedback(element: HTMLDivElement, message: string, type: 'error' | 'success' | 'none'): void {
        if (!element) {
            console.error("The element passed to showFeedback is null.");
            return;
        }

        let feedbackDiv = element.querySelector('.feedback-message') as HTMLDivElement;

        if (message) {
            if (!feedbackDiv) {
                feedbackDiv = document.createElement('div');
                feedbackDiv.className = 'feedback-message';
                element.appendChild(feedbackDiv);
            }

            feedbackDiv.innerHTML = message;
            if (type === 'error') {
                feedbackDiv.classList.add('error-message');
                feedbackDiv.classList.remove('success-message');
            } else if (type === 'success') {
                feedbackDiv.classList.add('success-message');
                feedbackDiv.classList.remove('error-message');
            }
        } else {
            if (feedbackDiv) {
                feedbackDiv.remove();
            }
        }

        element.querySelectorAll('input, select, textarea').forEach(input => {
            if (type === 'error') {
                input.classList.add('error');
            } else {
                input.classList.remove('error');
            }
        });
    }


    private async checkStudentExists(): Promise<void> {
        const studentNumber = this.studentNumberInput.value.trim();
        if (!studentNumber || studentNumber.length < 4) {
            this.showFeedback(this.studentNumberFeedback, 'Student number must be at least 4 characters', 'error');
            return;
        }

        this.showFeedback(this.studentNumberFeedback, 'Checking student number...', 'none');
        try {
            this.isExistingStudent = await ApplicationService.instance.checkExists(studentNumber);
            if (this.isExistingStudent) {
                this.showFeedback(this.studentNumberFeedback, 'Student found in system', 'success');
                this.toggleExistingStudentFields(true);
            } else {
                this.showFeedback(this.studentNumberFeedback, 'New student - all fields required', 'success');
                this.toggleExistingStudentFields(false);
            }
        } catch (error) {
            this.showFeedback(this.studentNumberFeedback, 'Could not verify student number', 'error');
        }
    }

    private toggleExistingStudentFields(isExisting: boolean): void {
        const fieldsToToggle = [
            this.identityTypeGroup, this.idNumberGroup, this.phoneNumberGroup, this.residentialAddressGroup,
            this.guardianNameGroup, this.guardianPhoneGroup, this.guardianWorkGroup, this.guardianEmailGroup
        ];

        fieldsToToggle.forEach(group => {
            if (group) {
                group.classList.toggle('hide', isExisting);
                const input = group.querySelector('input, select, textarea') as HTMLInputElement;
                if (input) {
                    input.required = !isExisting;
                }
            }
        });

        const studentAlert = document.getElementById('studentAlert') as HTMLDivElement;
        studentAlert.classList.toggle('show', isExisting);
    }

    private validateEmailMatch(): void {
        if (this.emailInput.value === this.confirmEmailInput.value) {
            this.showFeedback(this.emailMatchFeedback, 'Email addresses match', 'success');
        } else {
            this.showFeedback(this.emailMatchFeedback, 'Email addresses do not match', 'error');
        }
    }

    /**
     * Mirrors IdentityNumberValidator on the server. Worth duplicating: a typo caught here costs the
     * applicant one keystroke, whereas one that reaches the server creates a second student record
     * for the same person, because id_number is unique and a wrong ID simply does not collide.
     */
    private validateIdNumber(): boolean {
        const type = this.identityTypeSelect?.selected || 'SA_ID';
        const value = (this.idNumberInput.value || '').replace(/[\s-]/g, '');

        this.idNumberLabel.textContent = type === 'SA_ID' ? 'ID Number' : 'Document Number';
        this.idNumberInput.maxLength = type === 'SA_ID' ? 13 : 20;

        if (!value) {
            this.showFeedback(this.idNumberFeedback, '', 'none');
            return false;
        }

        const error = type === 'SA_ID' ? this.validateSaId(value) : this.validateDocumentNumber(value);
        if (error) {
            this.showFeedback(this.idNumberFeedback, error, 'error');
            return false;
        }
        this.showFeedback(this.idNumberFeedback, type === 'SA_ID' ? 'ID number looks valid' : 'Document number accepted', 'success');
        return true;
    }

    private validateSaId(value: string): string | null {
        if (!/^\d{13}$/.test(value)) {
            return 'A South African ID number must be exactly 13 digits. Using a passport? Change the document type above.';
        }
        const month = parseInt(value.substring(2, 4), 10);
        const day = parseInt(value.substring(4, 6), 10);
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return 'That ID number contains an impossible date of birth. Please check the first 6 digits.';
        }
        // Luhn over the first 12 digits, compared against the 13th.
        let sum = 0;
        for (let i = 0; i < 12; i++) {
            let digit = parseInt(value.charAt(11 - i), 10);
            if (i % 2 === 0) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
        }
        if ((10 - (sum % 10)) % 10 !== parseInt(value.charAt(12), 10)) {
            return 'That ID number is not valid — please check it for a typo.';
        }
        return null;
    }

    private validateDocumentNumber(value: string): string | null {
        if (value.length < 5 || value.length > 20) {
            return 'Please enter a valid document number (5 to 20 characters).';
        }
        if (!/^[A-Za-z0-9]+$/.test(value)) {
            return 'Document numbers may only contain letters and numbers.';
        }
        return null;
    }

    private updatePaymentDependentFields(): void {
        const paymentMethod = this.methodOfPaymentSelect.selected;
        const isCash = paymentMethod === 'cash';
        const isBursary = paymentMethod === 'bursary';

        this.budgetGroup.classList.toggle('hide', !isCash);
        this.bursaryGroup.classList.toggle('hide', !isBursary);

        (this.budgetInput.required as boolean) = isCash;
        (this.bursaryInput.required as boolean) = isBursary;
    }

    /**
     * "Where did you hear about us?" asks for detail only when the answer is a person rather than a
     * channel — a name to thank, or something we do not have an option for yet.
     *
     * <p>Matched loosely on purpose. The options are database rows a client can rename, and this
     * check previously compared against the exact label {@code 'Friend'}: renaming that row to
     * "Friend / Referral" would have silently stopped the form ever asking for the detail, with
     * nothing to notice until somebody wondered why the field went quiet.</p>
     */
    private updateRefererDependentFields(): void {
        const referer = (this.refererSelect.selectedText ?? '').toLowerCase();
        const needsDetails = ['other', 'friend', 'referral'].some(word => referer.includes(word));
        this.refererDetailsGroup.classList.toggle('hide', !needsDetails);
        (this.refererDetailsTextarea.required as boolean) = needsDetails;
    }

    private async updatePreference(buildingName: string | null): Promise<void> {
        if(!buildingName){
            this.preferenceSelect.clear();
            this.preferenceSelect.refresh();
            return;
        }
        try {
            const data = await ApplicationService.instance.checkAvailability(buildingName, this.yearInput.value || undefined);

            this.preferenceSelect.clear();
            this.preferenceSelect.updateOptions({
                items: data.map((type: any) => {
                    return {
                        value: type.id,
                        text: `${type.name} (${type.available} Available)`,
                        data: { value: type.id, text: type.name }
                    }
                })
            })
        } catch (error) {
            this.showCustomModal('Error', 'Error fetching room preferences. Please try again.', 'error');
            console.error("Failed to update preferences:", error);
        }
    }

    // ---------------------------------------------------------------- P2: building grid

    /**
     * Wires the photo-card grid when the per-client toggle rendered it. The hidden classic
     * selects stay the single source of truth — the grid only writes into them — so submit,
     * validation and the review step read exactly what they always did.
     */
    /** 1 when the residence grid is its own step (grid clients), 0 on the classic form. */
    private get gridStepOffset(): number {
        return this.buildingGridEl ? 1 : 0;
    }

    private initBuildingGrid(): void {
        if (!this.buildingGridEl) return;
        const cards = this.gridCards();

        // The native select auto-selects its first option (no placeholder row), which is the
        // default-choice bias this grid exists to remove. Start unchosen; only a real
        // ?building= deep link (the website widget's per-building apply links) survives —
        // and even then the room type is still the applicant's to pick in the sheet.
        const deepLink = new URLSearchParams(window.location.search).get('building');
        const linked = deepLink ? cards.find(c => c.dataset.building === deepLink) : undefined;
        if (linked) {
            this.suppressBuildingTrack = true;
            this.buildingSelect.selected = linked.dataset.building!;
            this.suppressBuildingTrack = false;
        } else {
            this.buildingSelect.selected = null;
            this.updatePreference(null);
        }

        cards.forEach(card => {
            card.addEventListener('click', () => this.openBuildingSheet(card));
            card.addEventListener('keydown', e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openBuildingSheet(card);
                }
            });
            card.querySelector('.bg-alt-toggle')?.addEventListener('click', e => {
                e.stopPropagation();
                this.toggleAlternative(card.dataset.building!);
            });
        });

        // Search + room-type filter (rendered only when there is more than one building).
        const search = document.getElementById('buildingSearch') as HTMLInputElement | null;
        search?.addEventListener('input', () => this.filterGrid());
        if (document.getElementById('buildingTypeFilter')) {
            this.typeFilterSelect = MaterialSelect.init('#buildingTypeFilter', {
                onSelect: () => this.filterGrid(),
            });
        }

        this.syncGridState();
    }

    /** Narrow the cards by name substring and structural room-type offer. In place, so the
     *  search input keeps focus; availability stays per-year inside the sheet. */
    private filterGrid(): void {
        const query = (document.getElementById('buildingSearch') as HTMLInputElement | null)
            ?.value.trim().toLowerCase() ?? '';
        const typeId = this.typeFilterSelect?.selected || '';
        let visible = 0;
        this.gridCards().forEach(card => {
            const nameMatch = !query
                || (card.dataset.publicName || card.dataset.building || '').toLowerCase().includes(query);
            const typeMatch = !typeId
                || (card.dataset.roomTypes || '').split(',').includes(String(typeId));
            const show = nameMatch && typeMatch;
            card.style.display = show ? '' : 'none';
            if (show) visible++;
        });
        const noMatch = document.getElementById('buildingNoMatch');
        if (noMatch) noMatch.style.display = visible === 0 ? '' : 'none';
    }

    private gridCards(): HTMLElement[] {
        return this.buildingGridEl
            ? Array.from(this.buildingGridEl.querySelectorAll<HTMLElement>('.bg-card')) : [];
    }

    /** The cards mirror the hidden selects — never the other way around. */
    private syncGridState(): void {
        if (!this.buildingGridEl) return;
        const chosen = this.buildingSelect.selected;
        const alts = new Set(this.alternativeBuildingsSelect.selectedIds as string[]);
        this.gridCards().forEach(card => {
            const name = card.dataset.building!;
            card.classList.toggle('chosen', !!chosen && name === chosen);
            card.classList.toggle('alt', alts.has(name) && name !== chosen);
        });
    }

    /**
     * The sheet's Choose CTA: building and room type land together. The select's onSelect
     * does the rest (preference reload, tracking, grid sync); the type is applied once that
     * reload settles, then the form moves on to the Accommodation step by itself.
     */
    private async chooseBuilding(name: string, typeId: number): Promise<void> {
        this.buildingSelect.selected = name;
        // Chosen and "also consider" are mutually exclusive.
        const alts = (this.alternativeBuildingsSelect.selectedIds as string[]).filter(v => v !== name);
        this.alternativeBuildingsSelect.selectedIds = alts;
        this.syncGridState();

        try {
            await this.preferenceLoad;
        } catch {
            // updatePreference already told the applicant; the type set below will warn unmatched.
        }
        this.preferenceSelect.selected = typeId;

        // Choosing IS completing this step — advance without a second tap. (The Residence
        // step sits at 3, after the Accommodation inputs — owner's order, 2026-08-19.)
        if (this.currentStep === 3 && this.gridStepOffset === 1) {
            this.nextStep();
        }
    }

    private toggleAlternative(name: string): void {
        if (name === this.buildingSelect.selected) return;
        const current = this.alternativeBuildingsSelect.selectedIds as string[];
        this.alternativeBuildingsSelect.selectedIds = current.includes(name)
            ? current.filter(v => v !== name)
            : [...current, name];
        this.syncGridState();
    }

    /**
     * The detail sheet — the in-house MaterialModal with a supplied element (its own template
     * is a full-height fixed-footer shell). Built once; content is set per open because
     * reassigning `content` wipes the modal's body.
     */
    private sheet(): MaterialModal {
        if (this.buildingSheet) return this.buildingSheet;
        const el = document.createElement('div');
        el.className = 'modal bg-sheet-modal';
        el.id = 'building-sheet';
        // PanelForm requires a .modal-content wrapper inside the supplied element.
        el.innerHTML = `
            <div class="modal-content">
                <div class="bg-sheet-media">
                    <button type="button" class="modal-close bg-sheet-close" aria-label="Close">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="bg-sheet-content">
                    <h3 class="bg-sheet-name"></h3>
                    <div class="bg-sheet-types">
                        <h4>Choose a room type</h4>
                        <div class="bg-sheet-type-list"></div>
                    </div>
                    <div class="bg-sheet-actions">
                        <button type="button" class="button button-primary bg-sheet-choose"></button>
                        <button type="button" class="button button-secondary bg-sheet-alt"></button>
                    </div>
                </div>
            </div>`;
        document.body.appendChild(el);
        this.buildingSheet = new MaterialModal(el, {});
        el.querySelector('.bg-sheet-choose')!.addEventListener('click', () => {
            if (this.sheetBuilding && this.sheetTypeId != null) {
                this.chooseBuilding(this.sheetBuilding, this.sheetTypeId);
                this.buildingSheet!.close();
            }
        });
        el.querySelector('.bg-sheet-alt')!.addEventListener('click', () => {
            if (this.sheetBuilding) this.toggleAlternative(this.sheetBuilding);
            this.buildingSheet!.close();
        });
        return this.buildingSheet;
    }

    private async openBuildingSheet(card: HTMLElement): Promise<void> {
        const name = card.dataset.building!;
        const publicName = card.dataset.publicName || name;
        const sheet = this.sheet();
        const el = sheet.getModal();
        this.sheetBuilding = name;
        this.track('BUILDING_VIEWED', {building: name});

        // Media: the building's photo, or the same placeholder tile the card shows.
        const media = el.querySelector('.bg-sheet-media')!;
        media.querySelector('img, .bg-placeholder')?.remove();
        if (card.dataset.hasImage === 'true') {
            const img = document.createElement('img');
            img.src = `/api/accommodation/buildings/image/${encodeURIComponent(name)}`;
            img.alt = '';
            media.prepend(img);
        } else {
            const placeholder = document.createElement('div');
            placeholder.className = 'bg-placeholder';
            placeholder.textContent = publicName.charAt(0);
            media.prepend(placeholder);
        }

        el.querySelector('.bg-sheet-name')!.textContent = publicName;

        const chosen = this.buildingSelect.selected === name;
        const isAlt = (this.alternativeBuildingsSelect.selectedIds as string[]).includes(name);
        const chooseBtn = el.querySelector<HTMLButtonElement>('.bg-sheet-choose')!;
        chooseBtn.textContent = `Choose ${publicName}`;
        chooseBtn.disabled = true; // enabled the moment a room type is picked
        const altBtn = el.querySelector<HTMLButtonElement>('.bg-sheet-alt')!;
        altBtn.textContent = isAlt ? 'Remove from also considering' : 'Also consider';
        altBtn.style.display = chosen ? 'none' : '';

        // The room type is chosen HERE (there is no dropdown on the Accommodation step for
        // grid clients), so the rows are the real input, not decoration. Counts follow the
        // applicant's chosen year. Fired BEFORE open(): open()'s promise resolves at the end
        // of the open animation, which a throttled background window never finishes.
        this.sheetTypeId = null;
        const list = el.querySelector('.bg-sheet-type-list')!;
        list.innerHTML = '<span class="bg-sheet-empty">Checking availability…</span>';
        const availability = ApplicationService.instance.checkAvailability(name, this.yearInput.value || undefined);
        sheet.open({building: name});
        try {
            const data = await availability;
            if (this.sheetBuilding !== name) return; // the applicant moved on to another card
            list.innerHTML = '';
            if (!data.length) {
                list.innerHTML = '<span class="bg-sheet-empty">No rooms are offered here for your chosen year — have a look at another residence.</span>';
                return;
            }
            const pick = (row: HTMLElement, typeId: number) => {
                list.querySelectorAll('.bg-sheet-type-row').forEach(r => r.classList.remove('selected'));
                row.classList.add('selected');
                this.sheetTypeId = typeId;
                chooseBtn.disabled = false;
            };
            data.forEach((type: any) => {
                const row = document.createElement('div');
                row.className = 'bg-sheet-type-row';
                row.setAttribute('role', 'radio');
                const radio = document.createElement('span');
                radio.className = 'bg-type-radio';
                const label = document.createElement('span');
                label.className = 'bg-sheet-type-name';
                label.textContent = type.name;
                const count = document.createElement('span');
                count.className = 'bg-sheet-type-count';
                count.textContent = `${type.available} available`;
                row.append(radio, label, count);
                row.addEventListener('click', () => pick(row, type.id));
                list.appendChild(row);
                // Preselect what the applicant already indicated: this building's current
                // room preference when reopening it, else the grid's room-type filter.
                const current = chosen ? this.preferenceSelect.selected : this.typeFilterSelect?.selected;
                if (current != null && String(current) === String(type.id)) pick(row, type.id);
            });
        } catch {
            list.innerHTML = '<span class="bg-sheet-empty">Could not check availability right now.</span>';
        }
    }

    private populateReviewStep(): void {
        this.reviewContent.innerHTML = '';

        // Personal Information Section
        const personalSection = this.createReviewSection('Personal Information', 'person', [
            { label: 'Application Year', value: this.yearInput.value || '' },
            { label: 'Student Number', value: this.studentNumberInput.value },
            { label: 'Full Name', value: `${this.firstNameInput.value} ${this.lastNameInput.value}` },
            { label: 'ID/Passport', value: this.idNumberInput.value || 'On file' },
            { label: 'Gender', value: this.genderSelect.selectedText || '' },
            { label: 'Email', value: this.emailInput.value },
            { label: 'Student Email', value: this.studentEmailInput.value },
            { label: 'Phone', value: this.phoneNumberInput.value || 'On file' },
            { label: 'WhatsApp', value: this.whatsappNumberInput.value },
            { label: 'Address', value: this.residentialAddressInput.value || 'On file' }
        ], 1);
        this.reviewContent.appendChild(personalSection);

        // Accommodation Section
        const accommodationSection = this.createReviewSection('Accommodation Preferences', 'bed', [
            { label: 'Course', value: this.courseInput.value },
            { label: 'Year of Study', value: this.yearOfStudySelect.selectedText || '' },
            { label: 'Campus', value: this.campusSelect.selectedText || '' },
            { label: 'Building', value: this.buildingSelect.selectedText || '' },
            { label: 'Alternative Buildings', value: (this.alternativeBuildingsSelect.selectedIds).join(', ') || 'None' },
            { label: 'Room Type', value: this.preferenceSelect.selectedText || '' },
            { label: 'Payment Method', value: this.methodOfPaymentSelect.selectedText || '' },
            { label: 'Preferred Move-In Date', value: this.preferredMoveInDate.input.value || '' },
            { label: 'Budget', value: this.budgetInput.value || 'N/A' },
            { label: 'Bursary', value: this.bursaryInput.value || 'N/A' },
            { label: 'Special Requirements', value: this.specialRequirementsTextArea.value || 'None' },
            { label: 'Referral Source', value: this.refererSelect.selectedText || '' },
            { label: 'Referral Details', value: this.refererDetailsTextarea.value || 'None' }
        ], 2); // Accommodation sits at 2 in both layouts; only steps after Residence shift.
        this.reviewContent.appendChild(accommodationSection);

        // Guardian Information Section (only show if not existing student)
        if (!this.isExistingStudent) {
            const guardianSection = this.createReviewSection('Guardian Information', 'family_restroom', [
                { label: 'Guardian Name', value: this.guardianNameInput.value },
                { label: 'Guardian Phone', value: this.guardianPhoneInput.value },
                { label: 'Guardian Work', value: this.guardianWorkInput.value },
                { label: 'Guardian Email', value: this.guardianEmailInput.value }
            ], 3 + this.gridStepOffset);
            this.reviewContent.appendChild(guardianSection);
        }
    }

    private createReviewSection(title: string, icon: string, items: { label: string, value: string }[], stepNumber: number): HTMLDivElement {
        const section = document.createElement('div');
        section.className = 'review-section';

        let html = `
            <h3><i class="material-icons">${icon}</i> ${title} <a href="#" class="edit-link" data-step="${stepNumber}">Edit</a></h3>
        `;

        items.forEach(item => {
            const displayValue = item.value === 'nsfas' ? 'NSFAS' :
                item.value === 'cash' ? 'Cash' :
                    item.value === 'bursary' ? 'Bursary' : item.value;

            if (displayValue && displayValue !== 'N/A' && displayValue !== 'None') {
                html += `
                    <div class="review-item">
                        <span class="review-label">${item.label}:</span>
                        <span class="review-value">${displayValue}</span>
                    </div>
                `;
            }
        });

        section.innerHTML = html;
        section.querySelector('.edit-link')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToStep(stepNumber);
        });

        return section;
    }

    public goToStep(stepNumber: number): void {
        this.currentStep = stepNumber;
        this.updateStepUI();
        this.trackStep();
    }

    public async submitApplication(): Promise<void> {
        if (!this.validateSignature()) {
            return;
        }

        const submitBtn = document.getElementById('submitApplicationBtn') as HTMLButtonElement;
        const originalHtml = submitBtn.innerHTML;
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<div class="loading"><div class="spinner"></div>Submitting...</div>';

        try {
            // Inside the try. Built outside it, anything this threw escaped submitApplication
            // entirely: no dialog, no finally, and the button sat on "Submitting..." for ever.
            const formData = this.generateFormData();

            const response: any = await ApplicationService.instance.submit(formData);

            // The server answers a duplicate submission with JSON ({resubmission, message})
            // instead of the plain-text success body: nothing new was saved, and saying
            // "Success" here is how nine applications went missing without anyone knowing.
            if (response?.resubmission) {
                Swal.fire('Already applied', response.message, 'info');
                return;
            }

            if (this.walkIn) {
                window.open(`/?page=/students/view/${this.studentNumberInput.value}?generate-qr=${response.responseText}`, '_blank');
                location.reload();
                return;
            }
            Swal.fire('Success', response.responseText, 'success');
        } catch (error: any) {
            // `${error.responseText}` rendered the literal string "undefined" for every failure
            // that was not a plain-text server body — a JS exception here, or any JSON error body
            // (GlobalExceptionHandler answers {message, details, stackTrace}, which has no
            // responseText). Applicants reported "Error / undefined" and it told us nothing.
            Swal.fire('Error', Application.describeFailure(error), 'error');
            // The server cannot log what never reached it. Report it so the next occurrence is
            // diagnosable instead of being reconstructed from a screenshot.
            Application.reportFailure(error, this.studentNumberInput.value, this.attemptId);
        } finally {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalHtml;
        }
    }

    /** The most useful sentence we can put in front of the applicant for any failure shape. */
    private static describeFailure(error: any): string {
        if (error?.networkError) {
            return 'We could not reach the server. Please check your connection and try again — '
                + 'your answers are still on this page.';
        }
        if (typeof error?.responseText === 'string' && error.responseText.trim()) return error.responseText;
        // GlobalExceptionHandler's JSON body.
        if (typeof error?.message === 'string' && error.message.trim()) return error.message;
        if (typeof error?.details === 'string' && error.details.trim()) return error.details;
        return 'Something went wrong while submitting. Please try again, and tell us your student '
            + 'number if it keeps happening.';
    }

    /** Best-effort; never let reporting a failure cause another one. */
    private static reportFailure(error: any, studentNumber: string, attemptId?: string): void {
        try {
            const detail = {
                studentNumber,
                attemptId: attemptId ?? null,
                status: error?.status ?? null,
                networkError: !!error?.networkError,
                name: error?.name ?? null,
                message: error?.message ?? error?.details ?? null,
                responseText: typeof error?.responseText === 'string'
                    ? error.responseText.slice(0, 500) : null,
                stack: typeof error?.stack === 'string' ? error.stack.slice(0, 1000) : null,
                userAgent: navigator.userAgent,
                url: location.href,
            };
            navigator.sendBeacon?.('/apply/diagnostic',
                new Blob([JSON.stringify(detail)], {type: 'application/json'}));
        } catch {
            // Diagnostics must never break a submit.
        }
    }

    private validateSignature(): boolean {
        if (!this.signaturePad || this.signaturePad.isEmpty()) {
            this.showFeedback(document.getElementById('signatureFeedback') as HTMLDivElement, 'Please provide your signature before submitting', 'error');
            return false;
        }
        this.showFeedback(document.getElementById('signatureFeedback') as HTMLDivElement, '', 'none');
        return true;
    }

    private generateFormData(): FormData {
        const formData = new FormData();
        const append = (key: string, value: string | null) => {
            if (value !== null) formData.append(key, value);
        };

        // Joins the server's outcome events (received/refused/accepted) to this page load's funnel.
        append('attemptId', this.attemptId);
        append('year', this.yearInput.value || '');
        append('firstname', this.firstNameInput.value);
        append('lastname', this.lastNameInput.value);
        append('studentNumber', this.studentNumberInput.value);

        if (!this.isExistingStudent) {
            append('identityType', this.identityTypeSelect.selected || 'SA_ID');
            append('idNumber', this.idNumberInput.value);
            append('phoneNumber', this.phoneNumberInput.value);
            append('residentialAddress', this.residentialAddressInput.value);
            append('guardianName', this.guardianNameInput.value);
            append('guardianPhone', this.guardianPhoneInput.value);
            append('guardianWork', this.guardianWorkInput.value);
            append('guardianEmail', this.guardianEmailInput.value);
        }

        append('email', this.emailInput.value);
        append('studentEmail', this.studentEmailInput.value);
        append('whatsappNumber', this.whatsappNumberInput.value);
        append('gender', this.genderSelect.selected || '');
        append('course', this.courseInput.value);
        append('yearOfStudy', this.yearOfStudySelect.selected || '');
        append('campus', this.campusSelect.selected || '');
        append('building', this.buildingSelect.selected || '');
        append('preference', this.preferenceSelect.selected || '');
        append('methodOfPayment', this.methodOfPaymentSelect.selected || '');
        append('preferredMoveInDate', this.preferredMoveInDate.input.value);
        append('budget', this.budgetInput.value);
        append('bursary', this.bursaryInput.value);
        append('specialRequirements', this.specialRequirementsTextArea.value);
        append('referer', this.refererSelect.selected || '');
        append('refererDetails', this.refererDetailsTextarea.value);

        const selectedAlternatives = this.alternativeBuildingsSelect.selectedIds as string[];
        if (selectedAlternatives && selectedAlternatives.length > 0) {
            const alternativesString = selectedAlternatives.join(',');
            append('alternativeBuildings', alternativesString);
        }

        if (this.signaturePad && !this.signaturePad.isEmpty()) {
            const signatureBlob = this.signaturePad.toDataURL('image/png');
            formData.append('signature', signatureBlob);
            console.log('Signature data appended to form data.');
        } else {
            console.error("Signature pad is empty or not initialized.");
        }

        document.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach(fileInput => {
            if (fileInput.files && fileInput.files.length > 0) {
                const rawName = fileInput.name;
                let attachmentKey = rawName;
                if (rawName.startsWith("attachments[") && rawName.endsWith("]")) {
                    attachmentKey = rawName.substring(12, rawName.length - 1);
                }
                // The prepared blob, not fileInput.files[0] — that is still the original camera
                // photo, and sending it would undo the compression entirely. Falls back to the
                // raw file if preparation somehow did not run, so a submit is never lost.
                const original = fileInput.files[0];
                const prepared = this.preparedUploads.get(rawName) ?? original;
                formData.append(attachmentKey, prepared, original.name);
            }
        });

        return formData;
    }

    private showCustomModal(title: string, message: string, icon: 'error' | 'success' | 'warning' | 'info' | 'question'): void {
        Swal.fire({
            title: title,
            text: message,
            icon: icon,
            confirmButtonText: 'OK'
        });
    }

    public hideCustomModal(): void {
        Swal.close();
    }

    /**
     * A structurally valid SA ID, so the test autofill still passes the checksum. Random digits no
     * longer get through — which is the point of the validation, but it would break dev testing.
     */
    private randomValidSaId(): string {
        const pad = (n: number, width: number) => n.toString().padStart(width, '0');
        // 96..99 then 00..05. The wrap matters: padStart only ever adds characters, so a raw 100
        // would make a 14-digit "ID" that the validator then rejects.
        const yy = pad((Math.floor(Math.random() * 10) + 96) % 100, 2);
        const mm = pad(Math.floor(Math.random() * 12) + 1, 2);
        const dd = pad(Math.floor(Math.random() * 28) + 1, 2);
        const body = `${yy}${mm}${dd}${pad(Math.floor(Math.random() * 10000), 4)}${Math.random() < 0.5 ? 0 : 1}8`;

        let sum = 0;
        for (let i = 0; i < 12; i++) {
            let digit = parseInt(body.charAt(11 - i), 10);
            if (i % 2 === 0) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
        }
        return body + ((10 - (sum % 10)) % 10).toString();
    }

    private autofillForTesting(): void {
        const randomStudentNumber = Math.floor(20000000 + Math.random() * 90000000).toString().substring(0, 8);
        const randomIdNumber = this.randomValidSaId();

        if (this.yearButtons.length) {
            this.selectYear(this.yearButtons[this.yearButtons.length - 1]);
        }
        this.firstNameInput.value = 'John';
        this.lastNameInput.value = 'Doe';
        this.studentNumberInput.value = randomStudentNumber;
        this.idNumberInput.value = randomIdNumber;
        this.phoneNumberInput.value = '0821234567';
        this.whatsappNumberInput.value = '0821234567';
        this.residentialAddressInput.value = '123 Test Street, Testville, 1234';
        this.emailInput.value = 'test.user@example.com';
        this.confirmEmailInput.value = 'test.user@example.com';
        this.studentEmailInput.value = `${randomStudentNumber}@student.university.ac.za`;
        this.courseInput.value = 'BSc Information Technology';
        this.budgetInput.value = '5000';
        this.bursaryInput.value = 'Test Bursary';
        this.specialRequirementsTextArea.value = 'This is an automated test entry generated for testing purposes.';
        this.refererDetailsTextarea.value = 'Automated Test';
        this.guardianNameInput.value = 'Jane Doe';
        this.guardianPhoneInput.value = '0837654321';
        this.guardianWorkInput.value = '0119876543';
        this.guardianEmailInput.value = 'guardian.test@example.com';
        // The chosen year may have pushed minDate past today (a 2027 application's earliest
        // move-in is in 2027) — a date below the minimum is silently rejected by flatpickr.
        const minDate = this.preferredMoveInDate.config.minDate;
        this.preferredMoveInDate.setDate(minDate && minDate > new Date() ? minDate : new Date());

        const fillSelect = (select: MaterialSelect) => {
            if (select.items.length > 1) {
                const randomIndex = Math.floor(Math.random() * (select.items.length - 1)) + 1;
                select.selected = select.items[randomIndex];
            }
        };

        fillSelect(this.genderSelect);
        fillSelect(this.yearOfStudySelect);
        fillSelect(this.campusSelect);
        fillSelect(this.methodOfPaymentSelect);
        fillSelect(this.refererSelect);

        // Handle dependent dropdowns
        if (this.buildingSelect.items.length > 1) {
            const randomIndex = Math.floor(Math.random() * (this.buildingSelect.items.length - 1)) + 1;
            this.buildingSelect.selected = this.buildingSelect.items[randomIndex];
            this.updatePreference(this.buildingSelect.selected).then(() => {
                fillSelect(this.preferenceSelect);
            });
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // The static must be assigned too: every global shim below reads the module-scoped
    // Application.instance, which was never set — only the window copy was — so hideModal()
    // (the one shim the template still calls) threw on undefined.
    Application.instance = new Application();
    // This is a workaround to make the instance accessible globally for event listeners
    (window as any).Application = {
        instance: Application.instance,
    };

    // Global functions for the HTML to call
    (window as any).nextStep = () => Application.instance.nextStep();
    (window as any).prevStep = () => Application.instance.prevStep();
    (window as any).submitApplication = () => Application.instance.submitApplication();
    (window as any).clearSignature = () => Application.instance.clearSignature();
    (window as any).goToStep = (step: number) => Application.instance.goToStep(step);
    (window as any).hideModal = () => Application.instance.hideCustomModal();
});
